import {
  buildExpensesWritePayload,
  isExpensesChanged,
  mapExpensesToFormRows,
} from '#/modules/momMgmt/workOrder/helper/workOrderExpenses';

function pickMtr(row, nestedKey, idKey) {
  const nested = row?.[nestedKey];
  if (nested && typeof nested === 'object') {
    return {
      id: nested.id ?? row?.[idKey] ?? '',
      mtrNo: nested.mtrNo ?? nested.code ?? '',
      name: nested.name ?? '',
    };
  }
  return {
    id: row?.[idKey] ?? '',
    mtrNo: '',
    name: '',
  };
}

function buildCurrentSubstitutionMap(materialSubstitutions) {
  const map = new Map();
  (materialSubstitutions ?? []).forEach((row) => {
    const source = pickMtr(row, 'sourceGroupMtr', 'sourceGroupMtrId');
    const to = pickMtr(row, 'toMtr', 'toMtrId');
    if (source.id) {
      map.set(source.id, to);
    }
  });
  return map;
}

function findAfterSubstitution(afterRows, sourceGroupMtrId) {
  return (afterRows ?? []).find((row) => {
    const source = pickMtr(row, 'sourceGroupMtr', 'sourceGroupMtrId');
    return source.id === sourceGroupMtrId;
  }) ?? null;
}

function toNumberOrZero(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * 工單目前數量＋替代料／既有替代 → 異動單表單。
 * from＝目前實際料（materialSubstitutions.to 或 BOM 選料）；to 預填相同，編輯草稿時用 after。
 * `changeEnabled` 僅前端：Create 全 false；Edit 在 after.to !== from 時 true。
 */
export function mapWorkOrderToAmendmentFormValues(workOrder, amendment = null) {
  const quantity = workOrder?.quantity ?? {};
  const after = amendment?.after;
  const currentByGroup = buildCurrentSubstitutionMap(workOrder?.materialSubstitutions);
  const afterRows = after?.mtrSubstitutions;

  const mtrSubstitutions = (
    workOrder?.bomMtrSelections ?? workOrder?.bomMaterialSelections ?? []
  ).map((selection) => {
    const group = pickMtr(selection, 'sourceGroupMtr', 'sourceGroupMaterialId');
    const selected = pickMtr(selection, 'selectedMtr', 'selectedMaterialId');
    const current = currentByGroup.get(group.id);
    const from = (current?.id ? current : selected);
    const afterRow = group.id ? findAfterSubstitution(afterRows, group.id) : null;
    const afterTo = afterRow ? pickMtr(afterRow, 'toMtr', 'toMtrId') : null;
    const toMtrId = afterTo?.id || from.id;
    const changeEnabled = Boolean(amendment) && Boolean(toMtrId) && toMtrId !== from.id;

    return {
      sourceGroupMtrId: group.id,
      sourceGroupMtrNo: group.mtrNo,
      sourceGroupMtrName: group.name,
      fromMtrId: from.id,
      fromMtrNo: from.mtrNo,
      fromMtrName: from.name,
      toMtrId,
      remark: afterRow?.remark ?? '',
      changeEnabled,
    };
  }).filter((row) => row.sourceGroupMtrId);

  // migration 前的異動單沒有費用快照，不可加入費用變更（須重建）
  const expensesLocked = amendment?.expenseSnapshotCaptured === false;
  const expenseSource = amendment && !expensesLocked && Array.isArray(after?.expenses)
    ? after.expenses
    : workOrder?.expenses;

  return {
    scrapQty: after?.scrapQty ?? quantity.scrapQty ?? 0,
    cancelQty: after?.cancelQty ?? quantity.cancelQty ?? 0,
    manufacturingQty: quantity.manufacturingQty ?? 0,
    completedQty: quantity.completedQty ?? 0,
    issueReason: amendment?.issueReason ?? '',
    remark: amendment?.remark ?? '',
    mtrSubstitutions,
    expenses: mapExpensesToFormRows(expenseSource),
    expensesLocked,
  };
}

function hasExpenseChanges(values, workOrder) {
  if (values?.expensesLocked) {
    return false;
  }
  return isExpensesChanged(values?.expenses, mapExpensesToFormRows(workOrder?.expenses));
}

/**
 * 表單 → write body。`mtrSubstitutions` 只送有變更列（changeEnabled 且 to ≠ from）。
 * 費用與工單有效費用不同才送 `expenses`＋工單 `expensesRevision`；
 * 相同則省略（BE 以有效費用為擬議值，草稿既有費用變更也就撤回）。
 */
export function mapAmendmentFormToWriteValues(values, updatedAt, workOrder = null) {
  const mtrSubstitutions = (values?.mtrSubstitutions ?? [])
    .filter((row) => (
      row?.changeEnabled
      && row?.toMtrId
      && row.toMtrId !== row.fromMtrId
    ))
    .map((row) => ({
      sourceGroupMtrId: row?.sourceGroupMtrId ?? null,
      toMtrId: row?.toMtrId ?? null,
      remark: row?.remark ?? null,
    }));

  return {
    scrapQty: values?.scrapQty,
    cancelQty: values?.cancelQty,
    issueReason: values?.issueReason ?? null,
    remark: values?.remark ?? null,
    mtrSubstitutions,
    ...(hasExpenseChanges(values, workOrder) ? {
      expenses: buildExpensesWritePayload(values?.expenses),
      expensesRevision: workOrder?.expensesRevision ?? null,
    } : {}),
    updatedAt,
  };
}

/**
 * 相對工單現值：至少 scrap／cancel 有變、任一替代 changeEnabled 且 to≠from，或費用有變。
 */
export function hasWorkOrderAmendmentFormChanges(values, workOrder) {
  const quantity = workOrder?.quantity ?? {};
  const scrapChanged = (
    toNumberOrZero(values?.scrapQty) !== toNumberOrZero(quantity.scrapQty)
  );
  const cancelChanged = (
    toNumberOrZero(values?.cancelQty) !== toNumberOrZero(quantity.cancelQty)
  );
  const substitutionChanged = (values?.mtrSubstitutions ?? []).some((row) => (
    Boolean(row?.changeEnabled)
    && Boolean(row?.toMtrId)
    && row.toMtrId !== row.fromMtrId
  ));

  return scrapChanged || cancelChanged || substitutionChanged
    || hasExpenseChanges(values, workOrder);
}
